/**
 * ============================================
 * DISCONNECTED OVERLAY
 * ============================================
 * 
 * Full-screen overlay shown when backend is unreachable.
 * Shows retry countdown and manual retry button.
 */

import { useConnection } from '@/context/ConnectionContext';
import { WifiOff, RefreshCw, ServerCrash } from 'lucide-react';
import { BACKEND_URL } from '@/config';

export function DisconnectedOverlay() {
  const { isConnected, isChecking, lastError, retryCount, nextRetryIn, checkConnection } = useConnection();

  // Don't show if connected
  if (isConnected) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-8">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <ServerCrash className="h-20 w-20 text-red-500" />
            <WifiOff className="h-8 w-8 text-red-400 absolute -bottom-1 -right-1 bg-background rounded-full p-1" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-2">Backend Unreachable</h2>
        
        {/* Message */}
        <p className="text-muted-foreground mb-4">
          Unable to connect to Lokko Overseer Backend.
          <br />
          The service may be down or restarting.
        </p>

        {/* Backend URL */}
        <p className="text-xs text-muted-foreground mb-6 font-mono bg-muted/50 px-3 py-2 rounded-lg inline-block">
          {BACKEND_URL || window.location.origin}/api/v1/health
        </p>

        {/* Error details */}
        {lastError && (
          <p className="text-sm text-red-400 mb-4">
            Error: {lastError}
          </p>
        )}

        {/* Retry info */}
        <div className="mb-6">
          {isChecking ? (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Checking connection...</span>
            </div>
          ) : (
            <p className="text-muted-foreground">
              Retrying in <span className="font-bold text-foreground">{nextRetryIn}s</span>
              {retryCount > 0 && (
                <span className="text-xs ml-2">(attempt {retryCount})</span>
              )}
            </p>
          )}
        </div>

        {/* Manual retry button */}
        <button
          onClick={() => checkConnection()}
          disabled={isChecking}
          className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
        >
          <RefreshCw className={`h-4 w-4 ${isChecking ? 'animate-spin' : ''}`} />
          {isChecking ? 'Checking...' : 'Retry Now'}
        </button>

        {/* Tips */}
        <div className="mt-8 text-left text-xs text-muted-foreground bg-muted/30 rounded-lg p-4">
          <p className="font-semibold mb-2">Troubleshooting:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Check if the backend server is running</li>
            <li>Verify network connectivity</li>
            <li>Check if PM2 process is active on the server</li>
            <li>Review backend logs for errors</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
